/**
 * firebase.ts — Inicialização do Firebase Client SDK para o Órbita.
 *
 * Lê configuração exclusivamente de variáveis de ambiente (import.meta.env.VITE_FIREBASE_*).
 * Nenhuma chave real é hardcodada aqui. Preencha .env.local (gitignored) com os valores reais.
 *
 * Projeto: nectaris-338ef (compartilhado; isolamento por tenantId — ADR-002)
 * Auth: email/senha na fundação; claims custom via Admin SDK (ADR-003)
 */

import { initializeApp, getApps, type FirebaseApp } from "firebase/app";
import { getAuth, connectAuthEmulator, type Auth } from "firebase/auth";
import { initializeFirestore, connectFirestoreEmulator, type Firestore } from "firebase/firestore";

// Configuração lida de variáveis de ambiente — sem hardcode de valores reais
const firebaseConfig = {
  apiKey:            import.meta.env.VITE_FIREBASE_API_KEY as string,
  authDomain:        import.meta.env.VITE_FIREBASE_AUTH_DOMAIN as string,
  projectId:         import.meta.env.VITE_FIREBASE_PROJECT_ID as string,
  storageBucket:     import.meta.env.VITE_FIREBASE_STORAGE_BUCKET as string,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID as string,
  appId:             import.meta.env.VITE_FIREBASE_APP_ID as string,
};

// Inicializa apenas uma vez (hot-reload safe)
const app: FirebaseApp =
  getApps().length === 0 ? initializeApp(firebaseConfig) : (getApps()[0] as FirebaseApp);

export const auth: Auth = getAuth(app);
// `ignoreUndefinedProperties`: campos opcionais ausentes (ex.: uma fonte de dados
// com preset mas sem `unidade`/`modeloImport`) chegam como `undefined`. Sem esta
// opção o Firestore REJEITA o write inteiro ("Unsupported field value: undefined"),
// que era a causa do "Não foi possível salvar a fonte". Omitir o campo undefined é
// exatamente a semântica desejada de um opcional ausente. Sem regressão: qualquer
// write que hoje funciona não tem undefined (senão já falharia).
export const db: Firestore = initializeFirestore(app, { ignoreUndefinedProperties: true });

// Emulador local: ativo quando VITE_USE_EMULATORS=true
// Nunca usar em produção; impede tráfego acidental para nectaris-338ef durante dev
if (
  import.meta.env.VITE_USE_EMULATORS === "true" &&
  import.meta.env.MODE !== "production"
) {
  console.info("[Órbita] Firebase Auth conectado ao emulador local (9099).");
  connectAuthEmulator(auth, "http://127.0.0.1:9099", { disableWarnings: true });
  console.info("[Órbita] Firebase Firestore conectado ao emulador local (8080).");
  connectFirestoreEmulator(db, "127.0.0.1", 8080);
}

export default app;
